/**
 * Modales CRUD pour ImmeubleDossierDetail :
 *   - PropertyEditModal (immeuble)
 *   - ApartmentEditModal (logement)
 *   - TenantEditModal (locataire)
 *   - ConfirmArchive (confirmation d'archivage générique)
 *
 * Extrait de ImmeubleDossierDetail.jsx (Batch #14.221).
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Loader2, Save, X, AlertTriangle, Trash2 } from "lucide-react";
import { Field } from "./MasterReadOnly";
import { API, authH } from "@/api";

const BUILDING_TYPES = [
  "duplex", "triplex", "quadruplex", "5+", "condo", "commercial", "autre",
];



function PropertyEditModal({ dossierId, mode, target, onClose, onSaved }) {
  const isNew = mode === "new";
  const [form, setForm] = useState({
    label: target?.label || "",
    civic_number: target?.civic_number || "",
    street: target?.street || "",
    city: target?.city || "",
    province: target?.province || "QC",
    postal_code: target?.postal_code || "",
    building_type: target?.building_type || "",
    year_built: target?.year_built ?? "",
    num_units: target?.num_units ?? "",
    purchase_price: target?.purchase_price ?? "",
    notes: target?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        label: form.label || null,
        civic_number: form.civic_number || null,
        street: form.street || null,
        city: form.city || null,
        province: form.province || null,
        postal_code: form.postal_code || null,
        building_type: form.building_type || null,
        year_built: form.year_built === "" ? null : Number(form.year_built),
        num_units: form.num_units === "" ? null : Number(form.num_units),
        purchase_price: form.purchase_price === "" ? null : Number(form.purchase_price),
        notes: form.notes || null,
      };
      const url = isNew
        ? `${API}/immeuble-dossiers/${dossierId}/properties`
        : `${API}/immeuble-dossiers/${dossierId}/properties/${target.id}`;
      const r = isNew
        ? await axios.post(url, payload, { headers: authH() })
        : await axios.put(url, payload, { headers: authH() });
      onSaved(r.data);
      toast.success(isNew ? "Propriété ajoutée" : "Propriété mise à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="immeuble-property-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50">
          <h3 className="font-bold">{isNew ? "Ajouter une propriété" : "Modifier la propriété"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded" data-testid="immeuble-property-modal-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          <Field label="Libellé / Nom de la propriété">
            <input value={form.label} onChange={(e) => upd("label", e.target.value)} maxLength={200} placeholder="ex: Plex Saint-Denis" className="cc-input" data-testid="immeuble-property-label" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Field label="Numéro civique"><input value={form.civic_number} onChange={(e) => upd("civic_number", e.target.value)} maxLength={20} className="cc-input" data-testid="immeuble-property-civic" /></Field>
            <div className="sm:col-span-3"><Field label="Rue"><input value={form.street} onChange={(e) => upd("street", e.target.value)} maxLength={200} className="cc-input" data-testid="immeuble-property-street" /></Field></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Ville"><input value={form.city} onChange={(e) => upd("city", e.target.value)} maxLength={100} className="cc-input" data-testid="immeuble-property-city" /></Field>
            <Field label="Province"><input value={form.province} onChange={(e) => upd("province", e.target.value)} maxLength={50} className="cc-input" data-testid="immeuble-property-province" /></Field>
            <Field label="Code postal"><input value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)} maxLength={20} placeholder="H2X 1Y2" className="cc-input font-mono" data-testid="immeuble-property-postal" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Field label="Type">
              <select value={form.building_type} onChange={(e) => upd("building_type", e.target.value)} className="cc-input" data-testid="immeuble-property-type">
                <option value="">—</option>
                {BUILDING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Année"><input type="number" value={form.year_built} onChange={(e) => upd("year_built", e.target.value)} min="1700" max="2100" className="cc-input font-mono" data-testid="immeuble-property-year" /></Field>
            <Field label="Nb. logements"><input type="number" value={form.num_units} onChange={(e) => upd("num_units", e.target.value)} min="0" max="1000" className="cc-input font-mono" data-testid="immeuble-property-num-units" /></Field>
            <Field label="Prix d'achat ($)"><input type="number" value={form.purchase_price} onChange={(e) => upd("purchase_price", e.target.value)} min="0" step="1000" className="cc-input font-mono" data-testid="immeuble-property-price" /></Field>
          </div>
          <Field label="Notes"><textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} maxLength={1000} className="cc-input" data-testid="immeuble-property-notes" /></Field>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
          <button onClick={submit} disabled={busy} className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="immeuble-property-modal-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {isNew ? "Ajouter" : "Enregistrer"}
          </button>
        </div>
        <style>{`
          .cc-input { width: 100%; padding: 0.55rem 0.75rem; border: 1px solid #e2e8f0; border-radius: 0.5rem; font-size: 0.875rem; outline: none; }
          .cc-input:focus { border-color: #10b981; box-shadow: 0 0 0 1px #10b981; }
        `}</style>
      </div>
    </div>
  );
}

function ApartmentEditModal({ dossierId, propId, mode, target, onClose, onSaved }) {
  const isNew = mode === "new";
  const [form, setForm] = useState({
    apt_number: target?.apt_number || "",
    bedrooms: target?.bedrooms ?? "",
    monthly_rent: target?.monthly_rent ?? "",
    surface_sqft: target?.surface_sqft ?? "",
    notes: target?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        apt_number: form.apt_number || null,
        bedrooms: form.bedrooms === "" ? null : Number(form.bedrooms),
        monthly_rent: form.monthly_rent === "" ? null : Number(form.monthly_rent),
        surface_sqft: form.surface_sqft === "" ? null : Number(form.surface_sqft),
        notes: form.notes || null,
      };
      const url = isNew
        ? `${API}/immeuble-dossiers/${dossierId}/properties/${propId}/apartments`
        : `${API}/immeuble-dossiers/${dossierId}/properties/${propId}/apartments/${target.id}`;
      const r = isNew
        ? await axios.post(url, payload, { headers: authH() })
        : await axios.put(url, payload, { headers: authH() });
      onSaved(r.data);
      toast.success(isNew ? "Appartement ajouté" : "Appartement mis à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="immeuble-apartment-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50">
          <h3 className="font-bold">{isNew ? "Ajouter un appartement" : "Modifier l'appartement"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded" data-testid="immeuble-apartment-modal-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="N° d'appartement"><input value={form.apt_number} onChange={(e) => upd("apt_number", e.target.value)} maxLength={50} placeholder="ex: 1, 2A, RDC…" className="cc-input" data-testid="immeuble-apartment-number" /></Field>
            <Field label="Chambres"><input type="number" value={form.bedrooms} onChange={(e) => upd("bedrooms", e.target.value)} min="0" max="20" className="cc-input font-mono" data-testid="immeuble-apartment-bedrooms" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Loyer mensuel ($)"><input type="number" value={form.monthly_rent} onChange={(e) => upd("monthly_rent", e.target.value)} min="0" step="10" className="cc-input font-mono" data-testid="immeuble-apartment-rent" /></Field>
            <Field label="Surface (pi²)"><input type="number" value={form.surface_sqft} onChange={(e) => upd("surface_sqft", e.target.value)} min="0" className="cc-input font-mono" data-testid="immeuble-apartment-surface" /></Field>
          </div>
          <Field label="Notes"><textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} maxLength={500} className="cc-input" data-testid="immeuble-apartment-notes" /></Field>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
          <button onClick={submit} disabled={busy} className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="immeuble-apartment-modal-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {isNew ? "Ajouter" : "Enregistrer"}
          </button>
        </div>
        <style>{`
          .cc-input { width: 100%; padding: 0.55rem 0.75rem; border: 1px solid #e2e8f0; border-radius: 0.5rem; font-size: 0.875rem; outline: none; }
          .cc-input:focus { border-color: #10b981; box-shadow: 0 0 0 1px #10b981; }
        `}</style>
      </div>
    </div>
  );
}

function TenantEditModal({ dossierId, propId, aptId, mode, target, onClose, onSaved }) {
  const isNew = mode === "new";
  const [form, setForm] = useState({
    first_name: target?.first_name || "",
    last_name: target?.last_name || "",
    email: target?.email || "",
    phone: target?.phone || "",
    move_in_date: target?.move_in_date || "",
    notes: target?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        email: form.email || null,
        phone: form.phone || null,
        move_in_date: form.move_in_date || null,
        notes: form.notes || null,
      };
      const url = isNew
        ? `${API}/immeuble-dossiers/${dossierId}/properties/${propId}/apartments/${aptId}/tenants`
        : `${API}/immeuble-dossiers/${dossierId}/properties/${propId}/apartments/${aptId}/tenants/${target.id}`;
      const r = isNew
        ? await axios.post(url, payload, { headers: authH() })
        : await axios.put(url, payload, { headers: authH() });
      onSaved(r.data);
      toast.success(isNew ? "Locataire ajouté" : "Locataire mis à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="immeuble-tenant-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50">
          <h3 className="font-bold">{isNew ? "Ajouter un locataire" : "Modifier le locataire"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded" data-testid="immeuble-tenant-modal-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Prénom"><input value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} maxLength={100} className="cc-input" data-testid="immeuble-tenant-first-name" /></Field>
            <Field label="Nom"><input value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} maxLength={100} className="cc-input" data-testid="immeuble-tenant-last-name" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Courriel"><input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} maxLength={200} className="cc-input" data-testid="immeuble-tenant-email" /></Field>
            <Field label="Téléphone"><input value={form.phone} onChange={(e) => upd("phone", e.target.value)} maxLength={50} placeholder="514-555-0100" className="cc-input font-mono" data-testid="immeuble-tenant-phone" /></Field>
          </div>
          <Field label="Date d'emménagement" hint="YYYY-MM-DD"><input type="date" value={form.move_in_date} onChange={(e) => upd("move_in_date", e.target.value)} className="cc-input font-mono" data-testid="immeuble-tenant-move-in" /></Field>
          <Field label="Notes"><textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} maxLength={500} className="cc-input" data-testid="immeuble-tenant-notes" /></Field>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
          <button onClick={submit} disabled={busy} className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="immeuble-tenant-modal-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {isNew ? "Ajouter" : "Enregistrer"}
          </button>
        </div>
        <style>{`
          .cc-input { width: 100%; padding: 0.55rem 0.75rem; border: 1px solid #e2e8f0; border-radius: 0.5rem; font-size: 0.875rem; outline: none; }
          .cc-input:focus { border-color: #10b981; box-shadow: 0 0 0 1px #10b981; }
        `}</style>
      </div>
    </div>
  );
}

function ConfirmArchive({ onClose, onConfirm }) {
  const [txt, setTxt] = useState("");
  const ok = txt.trim().toUpperCase() === "ARCHIVER";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="immeuble-archive-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800">Archiver ce dossier immeuble ?</h3>
            <p className="text-xs text-slate-500 mt-1">
              Le dossier est mis en archive (récupérable depuis la corbeille). La fiche Master n'est PAS affectée.
            </p>
          </div>
        </div>
        <label className="block text-xs text-slate-700 mt-3">
          Tapez <b className="font-mono text-rose-600">ARCHIVER</b> pour confirmer :
          <input
            value={txt}
            onChange={(e) => setTxt(e.target.value)}
            autoFocus
            className="w-full mt-1 px-3 py-2 border rounded-lg text-sm font-mono uppercase"
            data-testid="immeuble-archive-confirm-input"
          />
        </label>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border" data-testid="immeuble-archive-cancel">
            <X className="w-3.5 h-3.5 inline" /> Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={!ok}
            className="px-4 py-1.5 text-sm rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-30 inline-flex items-center gap-1.5"
            data-testid="immeuble-archive-confirm-btn"
          >
            <Trash2 className="w-3.5 h-3.5" /> Archiver
          </button>
        </div>
      </div>
    </div>
  );
}

export { PropertyEditModal, ApartmentEditModal, TenantEditModal, ConfirmArchive };
